import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

export default function ExamResultsDetails() {
  const { examId } = useParams();
  const navigate = useNavigate();
  
  const [exam, setExam] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchResults();
  }, [examId]);

  const fetchResults = async () => {
    setLoading(true);
    setError('');
    try {
      // 1. Fetch exam info
      const examRes = await fetch(`${API_BASE}/admin/exams/${examId}`);
      if (!examRes.ok) throw new Error('Failed to fetch exam info');
      const examData = await examRes.json();
      setExam(examData);

      // 2. Fetch student results
      const res = await fetch(`${API_BASE}/admin/review/exams/${examId}/students`);
      if (!res.ok) throw new Error('Failed to fetch results');
      const resultsData = await res.json();
      setResults(resultsData);
    } catch (err) {
      console.error('❌ Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ UPDATED: Calculate duration if duration_seconds is missing
  const formatTime = (durationSeconds, startedAt, completedAt) => {
    if (durationSeconds && durationSeconds > 0) {
      const mins = Math.floor(durationSeconds / 60);
      const secs = durationSeconds % 60;
      return `${mins}m ${secs}s`;
    }
    
    if (startedAt && completedAt) {
      const start = new Date(startedAt).getTime();
      const end = new Date(completedAt).getTime();
      const diffSeconds = Math.floor((end - start) / 1000);
      
      if (diffSeconds >= 0) {
        const mins = Math.floor(diffSeconds / 60);
        const secs = diffSeconds % 60;
        return `${mins}m ${secs}s`;
      }
    }
    
    return '0m 0s';
  };

  const getClassificationColor = (classification) => {
    const colors = {
      'Advanced Mastery': '#28a745',
      'Meeting Expectations': '#007BFF',
      'Basic Emerging': '#ffc107',
      'Foundationally Weak': '#dc3545'
    };
    return colors[classification] || '#666';
  };

  if (loading) return <div style={{ textAlign: 'center', marginTop: 60 }}>⏳ Loading results...</div>;
  if (error) return <div style={{ padding: 20, background: '#f8d7da', color: '#721c24', borderRadius: 8 }}>❌ {error}</div>;
  if (!exam) return <div style={{ padding: 20 }}>Exam not found</div>;

  const isDiagnostic = exam.diagnostic_config !== null;

  return (
    <div style={{ maxWidth: 1200, margin: '40px auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 15 }}>
        <div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: 'clamp(1.2rem, 4vw, 1.5rem)' }}>
            Exam Results: {exam.exam_name}
            {isDiagnostic && (
              <span style={{ 
                marginLeft: 10, 
                padding: '4px 12px', 
                background: '#e7f1ff', 
                color: '#0056b3', 
                borderRadius: 12, 
                fontSize: 12, 
                fontWeight: 600 
              }}>
                📊 Diagnostic
              </span>
            )}
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
            Subject: {exam.exam_topic} | Level: {exam.level} | Created: {new Date(exam.created_at).toLocaleString()}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={fetchResults} style={{ padding: '8px 16px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>🔄 Refresh</button>
          <button onClick={() => navigate(-1)} style={{ padding: '8px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' }}>← Back</button>
        </div>
      </div>

      <h2 style={{ marginBottom: 15, fontSize: '1.2rem' }}>Student Results ({results.length})</h2>
      
      {/* ✅ Mobile-responsive wrapper: allows horizontal scroll on small screens */}
      <div style={{ background: '#fff', border: '1px solid #ddd', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead style={{ background: '#f8f9fa' }}>
              <tr>
                <th style={{ padding: 12, textAlign: 'left', borderBottom: '2px solid #ddd' }}>Student</th>
                {isDiagnostic ? (
                  <>
                    <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Core Score</th>
                    <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Classification</th>
                  </>
                ) : (
                  <>
                    <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Score</th>
                    <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Questions</th>
                  </>
                )}
                <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Time Taken</th>
                <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Completed</th>
                <th style={{ padding: 12, textAlign: 'center', borderBottom: '2px solid #ddd' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {results.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#666' }}>
                    No completed attempts yet
                  </td>
                </tr>
              ) : (
                results.map((result, idx) => {
                  const coreScore = result.core_score !== null && result.core_score !== undefined 
                    ? Number(result.core_score) 
                    : null;
                  const score = result.score !== null && result.score !== undefined 
                    ? Number(result.score) 
                    : null;
                  
                  return (
                    <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 12 }}>
                        <div style={{ fontWeight: 600 }}>{result.first_name} {result.last_name}</div>
                        <div style={{ fontSize: 12, color: '#666' }}>{result.username}</div>
                      </td>
                      
                      {isDiagnostic ? (
                        <>
                          <td style={{ padding: 12, textAlign: 'center' }}>
                            <span style={{ 
                              fontWeight: 'bold', 
                              color: coreScore >= 80 ? '#28a745' : coreScore >= 60 ? '#ffc107' : '#dc3545' 
                            }}>
                              {coreScore !== null ? `${coreScore.toFixed(1)}%` : 'N/A'}
                            </span>
                          </td>
                          <td style={{ padding: 12, textAlign: 'center' }}>
                            {result.final_classification ? (
                              <span style={{
                                padding: '4px 12px',
                                background: getClassificationColor(result.final_classification),
                                color: '#fff',
                                borderRadius: 12,
                                fontSize: 13,
                                fontWeight: 600,
                                whiteSpace: 'nowrap'
                              }}>
                                {result.final_classification}
                              </span>
                            ) : (
                              <span style={{ color: '#999' }}>N/A</span>
                            )}
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ padding: 12, textAlign: 'center' }}>
                            <span style={{ 
                              fontWeight: 'bold', 
                              color: score >= 60 ? '#28a745' : '#dc3545' 
                            }}>
                              {score !== null ? `${score.toFixed(2)}%` : 'N/A'}
                            </span>
                          </td>
                          <td style={{ padding: 12, textAlign: 'center' }}>
                            {result.correct_count}/{result.total_questions}
                          </td>
                        </>
                      )}
                      
                      <td style={{ padding: 12, textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {formatTime(result.duration_seconds, result.started_at, result.completed_at)}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center', color: '#666', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {result.completed_at ? new Date(result.completed_at).toLocaleString() : '-'}
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        {/* ✅ Navigation link preserved exactly as it was */}
                        <button 
                          onClick={() => navigate(`/admin/attempt-details/${result.assignment_id}`)}
                          style={{ 
                            padding: '8px 14px', 
                            background: '#17a2b8', 
                            color: '#fff', 
                            border: 'none', 
                            borderRadius: 6, 
                            cursor: 'pointer', 
                            fontSize: 13,
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          View Attempt
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}